import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  roleChecked: boolean;
  credits: number;
  plan: string;
  avatarUrl: string;
  refreshCredits: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  roleChecked: false,
  credits: 0,
  plan: 'free',
  avatarUrl: '',
  refreshCredits: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false); // admin rol kontrolü tamamlandı mı (AdminRoute beklesin)
  const [credits, setCredits] = useState(0);
  const [plan, setPlan] = useState('free');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Profil fotoğrafını çek (header avatarında göstermek için)
  const refreshProfile = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('id', user.id)
        .maybeSingle();
      setAvatarUrl((data as { avatar_url?: string } | null)?.avatar_url || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Fetch user credits
  const refreshCredits = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits, plan')
        .eq('user_id', user.id)
        .single();
      
      if (data && !error) {
        setCredits(data.credits);
        setPlan(data.plan);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  // Set up realtime subscription for credit updates
  useEffect(() => {
    if (!user) return;

    // Initial fetch
    refreshCredits();
    refreshProfile();

    const channel = supabase
      .channel('user-credits-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Realtime credit update:', payload);
          const updatedCredits = payload.new as { credits: number; plan: string };
          setCredits(updatedCredits.credits);
          setPlan(updatedCredits.plan);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Track user presence
  useEffect(() => {
    if (!user) return;

    const updatePresence = async (status: 'online' | 'offline') => {
      try {
        await supabase
          .from('user_presence')
          .upsert({
            user_id: user.id,
            status,
            last_seen: new Date().toISOString(),
          }, {
            onConflict: 'user_id'
          });
      } catch (error) {
        console.error('Error updating presence:', error);
      }
    };

    // Set user as online when component mounts
    updatePresence('online');

    // Update presence every 30 seconds
    const interval = setInterval(() => {
      updatePresence('online');
    }, 30000);

    // Set user as offline when page is hidden or unloaded
    const handleVisibilityChange = () => {
      if (document.hidden) {
        updatePresence('offline');
      } else {
        updatePresence('online');
      }
    };

    const handleBeforeUnload = () => {
      updatePresence('offline');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      updatePresence('offline');
    };
  }, [user]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        // loading'i BURADA kapatma: dinleyici, oturum localStorage'dan geri yüklenmeden
        // session=null ile erken tetiklenebilir → korumalı sayfalar yanlışlıkla /auth'a atar.
        // Yetkili ilk kontrol aşağıdaki getSession().then; loading'i o kapatır.

        if (session?.user) {
          setTimeout(() => {
            checkAdminRole(session.user.id);
            claimPendingReferral();
            guardOAuthSignup(session.user);
          }, 0);
        } else {
          setIsAdmin(false);
          setRoleChecked(true);
          setAvatarUrl('');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        checkAdminRole(session.user.id);
      } else {
        setRoleChecked(true);
      }
    }).catch(() => { setLoading(false); setRoleChecked(true); });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();
      
      setIsAdmin(!!data && !error);
    } catch (error) {
      setIsAdmin(false);
    } finally {
      setRoleChecked(true);
    }
  };

  // Davet linkiyle gelip Google/e-posta ile giren kullanıcıyı davet edene bağla.
  // (Google kaydında ref_code metadata'ya geçmediği için referred_by boş kalıyordu.)
  const claimPendingReferral = async () => {
    const code = (localStorage.getItem("ref_code") || "").trim();
    if (!code) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("claim_referral", { p_ref_code: code });
      // Başarılı bağlandıysa ya da zaten bağlıysa kodu temizle (tekrar denemesin)
      if (!error && (data as { ok?: boolean })?.ok) localStorage.removeItem("ref_code");
    } catch (_) { /* sessiz: davet bağlama akışı asla girişi bloklamasın */ }
  };

  // Google (OAuth) ile YENİ kayıt olan kullanıcı için IP suistimal kontrolü.
  // Google kaydı, e-posta kaydındaki IP guard'ını ATLIYORDU → aynı IP'den sınırsız 40-kredi hesabı açılabiliyordu.
  // Sadece yeni Google hesabında (son 5 dk) ve bir kez çalışır; suistimal varsa edge krediyi sıfırlar. Asla girişi bloklamaz.
  const guardOAuthSignup = async (u: User) => {
    try {
      if (u.app_metadata?.provider !== "google") return; // yalnız Google OAuth
      const createdAt = u.created_at ? new Date(u.created_at).getTime() : 0;
      if (!createdAt || Date.now() - createdAt > 5 * 60 * 1000) return; // yalnız taze kayıt, her girişte değil
      const flagKey = `oauth_guard_${u.id}`;
      if (localStorage.getItem(flagKey)) return; // bir kez
      localStorage.setItem(flagKey, "1");
      await supabase.functions.invoke("oauth-guard", { body: { email: u.email || "" } });
    } catch (_) { /* sessiz: güvenlik guard'ı asla girişi bloklamasın */ }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, roleChecked, credits, plan, avatarUrl, refreshCredits, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
